/**
 * TrackManager - Manages track state including mute status and volumes
 */

interface TrackInfo {
  index: number;
  isMuted: boolean;
  volume: number;
  previousVolume: number;
}

export class TrackManager {
  private mutedTracks: Set<number> = new Set();
  private previousVolumes: Map<number, number> = new Map();
  private hasStartedPlaybackState = false;
  private readonly trackCount: number;

  constructor(trackCount: number) {
    this.trackCount = trackCount;
    this.initializeTracks();
  }

  /**
   * Initialize all tracks as muted with default volumes
   */
  private initializeTracks(): void {
    for (let i = 0; i < this.trackCount; i++) {
      this.mutedTracks.add(i);
      this.previousVolumes.set(i, 1.0);
    }
  }

  /**
   * Get the number of tracks
   */
  getTrackCount(): number {
    return this.trackCount;
  }

  /**
   * Check if a track is muted
   */
  isTrackMuted(trackIndex: number): boolean {
    if (trackIndex < 0 || trackIndex >= this.trackCount) {
      return true; // Consider invalid tracks as muted
    }
    return this.mutedTracks.has(trackIndex);
  }

  /**
   * Mute a specific track
   */
  muteTrack(trackIndex: number): void {
    if (trackIndex >= 0 && trackIndex < this.trackCount) {
      this.mutedTracks.add(trackIndex);
    }
  }

  /**
   * Unmute a specific track
   */
  unmuteTrack(trackIndex: number): void {
    if (trackIndex >= 0 && trackIndex < this.trackCount) {
      this.mutedTracks.delete(trackIndex);
    }
  }

  /**
   * Toggle mute state for a track
   */
  toggleMute(trackIndex: number): boolean {
    if (trackIndex < 0 || trackIndex >= this.trackCount) {
      return true; // Return muted state for invalid tracks
    }

    if (this.isTrackMuted(trackIndex)) {
      this.unmuteTrack(trackIndex);
      return false;
    } else {
      this.muteTrack(trackIndex);
      return true;
    }
  }

  /**
   * Check if any track is unmuted
   */
  hasAnyUnmutedTrack(): boolean {
    return this.mutedTracks.size < this.trackCount;
  }

  /**
   * Get all muted track indices
   */
  getMutedTracks(): Set<number> {
    return new Set(this.mutedTracks);
  }

  /**
   * Reset all tracks to muted state
   */
  resetAllMuted(): void {
    for (let i = 0; i < this.trackCount; i++) {
      this.mutedTracks.add(i);
    }
  }

  /**
   * Get previous volume for a track
   */
  getPreviousVolume(trackIndex: number): number {
    if (trackIndex < 0 || trackIndex >= this.trackCount) {
      return 1.0;
    }
    return this.previousVolumes.get(trackIndex) || 1.0;
  }

  /**
   * Set previous volume for a track
   */
  setPreviousVolume(trackIndex: number, volume: number): void {
    if (trackIndex >= 0 && trackIndex < this.trackCount) {
      this.previousVolumes.set(trackIndex, volume);
    }
  }

  /**
   * Get track volume (0 if muted, previous volume if unmuted)
   */
  getTrackVolume(trackIndex: number): number {
    if (this.isTrackMuted(trackIndex)) {
      return 0;
    }
    return this.getPreviousVolume(trackIndex);
  }

  /**
   * Check if playback has started
   */
  hasStartedPlayback(): boolean {
    return this.hasStartedPlaybackState;
  }

  /**
   * Set playback started state
   */
  setPlaybackStarted(started: boolean): void {
    this.hasStartedPlaybackState = started;
  }

  /**
   * Reset playback state
   */
  resetPlaybackState(): void {
    this.hasStartedPlaybackState = false;
  }

  /**
   * Get info for a specific track
   */
  getTrackInfo(trackIndex: number): TrackInfo {
    const isMuted = this.isTrackMuted(trackIndex);
    const previousVolume = this.getPreviousVolume(trackIndex);

    return {
      index: trackIndex,
      isMuted,
      volume: isMuted ? 0 : previousVolume,
      previousVolume
    };
  }

  /**
   * Get info for all tracks
   */
  getAllTracksInfo(): TrackInfo[] {
    const info: TrackInfo[] = [];
    for (let i = 0; i < this.trackCount; i++) {
      info.push(this.getTrackInfo(i));
    }
    return info;
  }
}
